import React, { useState } from "react";
import { FaWallet, FaEdit, FaCheck, FaTimes } from "react-icons/fa";
import axios from "axios";
import API from "../config/config";
import DocumentStatusEditor from "./DocumentStatusEditor";

const BankDetails = ({ bank, editable, driverId, currentUserRole }) => {
  const token = localStorage.getItem("adminToken");

  const noBankData =
    !bank ||
    (!bank.accountNumber?.trim() &&
      !bank.ifsc?.trim() &&
      !bank.beneficiaryName?.trim());

  const [editField, setEditField] = useState("");
  const [formData, setFormData] = useState({
    accountNumber: bank?.accountNumber || "",
    ifsc: bank?.ifsc || "",
    beneficiaryName: bank?.beneficiaryName || "",
    bankName: bank?.bankName || "",
    status: bank?.status || "",
    statusReason: bank?.statusReason || "",
  });

  if (noBankData) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSaveField = async (field) => {
    try {
      const payload = {};

      if (field === "status") {
        payload.status = formData.status;
        if (formData.status === "Rejected" || formData.status === "Revisit") {
          payload.statusReason = formData.statusReason;
        }
      } else {
        payload[field] = formData[field];
      }

      await axios.patch(`${API.ADMIN.BANK_DETAILS}/${bank._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setEditField("");
    } catch (err) {
      console.error("Bank update failed:", err);
      alert("Failed to update bank details.");
    }
  };

  return (
    <section className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <FaWallet className="text-red-600 text-2xl" />
        <h2 className="text-2xl font-semibold text-gray-800">Bank Details</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Account Number */}
        <div>
          <strong>Account Number:</strong>{" "}
          {editField === "accountNumber" ? (
            <span className="flex gap-2 items-center">
              <input
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={() => handleSaveField("accountNumber")}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setFormData((p) => ({
                    ...p,
                    accountNumber: bank.accountNumber,
                  }));
                  setEditField("");
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.accountNumber}
              {editable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditField("accountNumber")}
                />
              )}
            </span>
          )}
        </div>

        {/* IFSC */}
        <div>
          <strong>IFSC:</strong>{" "}
          {editField === "ifsc" ? (
            <span className="flex gap-2 items-center">
              <input
                name="ifsc"
                value={formData.ifsc}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={() => handleSaveField("ifsc")}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setFormData((p) => ({ ...p, ifsc: bank.ifsc }));
                  setEditField("");
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.ifsc}
              {editable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditField("ifsc")}
                />
              )}
            </span>
          )}
        </div>

        {/* Beneficiary Name */}
        <div>
          <strong>Beneficiary Name:</strong>{" "}
          {editField === "beneficiaryName" ? (
            <span className="flex gap-2 items-center">
              <input
                name="beneficiaryName"
                value={formData.beneficiaryName}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={() => handleSaveField("beneficiaryName")}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setFormData((p) => ({
                    ...p,
                    beneficiaryName: bank.beneficiaryName,
                  }));
                  setEditField("");
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.beneficiaryName}
              {editable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditField("beneficiaryName")}
                />
              )}
            </span>
          )}
        </div>

        {/* Bank Name */}
        <div>
          <strong>Bank Name:</strong>{" "}
          {editField === "bankName" ? (
            <span className="flex gap-2 items-center">
              <input
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                className="border px-2 py-1 rounded text-sm"
              />
              <FaCheck
                className="text-green-600 cursor-pointer"
                onClick={() => handleSaveField("bankName")}
              />
              <FaTimes
                className="text-red-600 cursor-pointer"
                onClick={() => {
                  setFormData((p) => ({
                    ...p,
                    bankName: bank.bankName,
                  }));
                  setEditField("");
                }}
              />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {formData.bankName}
              {editable && (
                <FaEdit
                  className="text-blue-600 cursor-pointer"
                  onClick={() => setEditField("bankName")}
                />
              )}
            </span>
          )}
        </div>
      </div>
      {/* Status */}
      <DocumentStatusEditor
        statusForm={formData}
        setStatusForm={setFormData}
        isEditing={editField === "status"}
        onEdit={() => setEditField("status")}
        onCancel={() => {
          setFormData((prev) => ({
            ...prev,
            status: bank.status,
            statusReason: bank.statusReason,
          }));
          setEditField("");
        }}
        onSave={() => handleSaveField("status")}
        editable={editable}
        userRole={currentUserRole}
      />
    </section>
  );
};

export default BankDetails;
